import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, MoreVertical, ShieldOff, Flag, Play, Pause, LifeBuoy, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import VoiceRecorder from '../components/messaging/VoiceRecorder';
import {
  listConversations, createConversation, createSupportConversation,
  getMessages, sendTextMessage, sendVoiceMessage, markConversationRead,
  blockUser, reportContent, connectMessagingSocket,
} from '../services/messaging';

const VoiceMessageBubble = ({ url }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={toggle} className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary flex-shrink-0">
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>
      <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} className="hidden" />
      <span className="text-sm text-muted-foreground">Message vocal</span>
    </div>
  );
};

const Messages = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const activeConversation = conversations.find((c) => c.id === activeId);

  const loadConversations = useCallback(async () => {
    try {
      const convos = await listConversations(token);
      setConversations(convos);
    } catch (e) {
      console.error('Erreur chargement conversations', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Connexion WebSocket temps réel
  useEffect(() => {
    if (!token) return;
    const ws = connectMessagingSocket(token);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'new_message') {
          setMessages((prev) => (data.message.conversation_id === activeId ? [...prev, data.message] : prev));
          loadConversations();
        }
      } catch (e) { /* ignore */ }
    };
    const pingInterval = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
    return () => { clearInterval(pingInterval); ws.close(); };
  }, [token, activeId]);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const msgs = await getMessages(activeId, token);
        setMessages(msgs);
        await markConversationRead(activeId, token);
        loadConversations();
      } catch (e) {
        console.error('Erreur chargement messages', e);
      }
    })();
  }, [activeId, token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeId) return;
    const content = input.trim();
    setInput('');
    try {
      const msg = await sendTextMessage(activeId, content, token);
      setMessages((prev) => [...prev, msg]);
      loadConversations();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'envoi du message");
    }
  };

  const handleSendVoice = async (blob) => {
    if (!activeId) return;
    try {
      const msg = await sendVoiceMessage(activeId, blob, token);
      setMessages((prev) => [...prev, msg]);
      loadConversations();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'envoi du message vocal");
    }
  };

  const handleNewConversation = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      const convo = await createConversation([newEmail.trim()], token);
      setShowNew(false);
      setNewEmail('');
      await loadConversations();
      setActiveId(convo.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Impossible de créer la conversation');
    }
  };

  const handleContactSupport = async () => {
    try {
      const convo = await createSupportConversation(token);
      await loadConversations();
      setActiveId(convo.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleBlock = async (email) => {
    if (!window.confirm(`Bloquer ${email} ? Vous ne pourrez plus échanger de messages.`)) return;
    await blockUser(email, token);
    setMenuOpenFor(null);
    loadConversations();
  };

  const handleReport = async (email) => {
    const reason = window.prompt('Décrivez brièvement le problème à signaler :');
    if (!reason) return;
    await reportContent({ targetEmail: email, reason }, token);
    setMenuOpenFor(null);
    alert('Signalement envoyé. Merci.');
  };

  const otherParticipant = (convo) => (convo.participants || []).find((p) => p !== user?.email) || convo.title || 'Conversation';

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-end mb-4">
          <Button variant="outline" size="sm" onClick={handleContactSupport} className="flex items-center gap-2" data-testid="contact-support-btn">
            <LifeBuoy className="h-4 w-4" /> Contacter le support
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh] border border-border rounded-xl overflow-hidden">
          {/* Liste des conversations */}
          <div className="border-r border-border overflow-y-auto bg-card/40">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">Messages</h2>
              <button onClick={() => setShowNew(!showNew)} className="text-primary hover:text-primary/80" title="Nouvelle conversation" data-testid="new-conversation-btn">
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {showNew && (
              <form onSubmit={handleNewConversation} className="p-3 border-b border-border flex gap-2">
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Email du destinataire"
                  className="flex-1 text-sm rounded-md border border-border px-2 py-1.5 bg-background"
                  data-testid="new-conversation-email"
                />
                <Button type="submit" size="sm">OK</Button>
              </form>
            )}
            {loading ? (
              <div className="p-4 text-sm text-muted-foreground">Chargement...</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">Aucune conversation pour l'instant.</div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-left p-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${activeId === c.id ? 'bg-muted' : ''}`}
                  data-testid={`conversation-item-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">
                      {c.type === 'support' ? '🛟 Support PADAID' : otherParticipant(c)}
                    </span>
                    {c.unread_count > 0 && (
                      <span className="bg-primary text-white text-xs rounded-full px-2 py-0.5">{c.unread_count}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-1">{c.last_message_preview || 'Nouvelle conversation'}</p>
                </button>
              ))
            )}
          </div>

          {/* Fil de discussion */}
          <div className="md:col-span-2 flex flex-col">
            {!activeConversation ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Sélectionnez une conversation pour commencer
              </div>
            ) : (
              <>
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <span className="font-semibold">
                    {activeConversation.type === 'support' ? '🛟 Support PADAID' : otherParticipant(activeConversation)}
                  </span>
                  {activeConversation.type !== 'support' && (
                    <div className="relative">
                      <button onClick={() => setMenuOpenFor(menuOpenFor === activeConversation.id ? null : activeConversation.id)} data-testid="conversation-menu-btn">
                        <MoreVertical className="h-5 w-5 text-muted-foreground" />
                      </button>
                      {menuOpenFor === activeConversation.id && (
                        <div className="absolute right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-10 w-48">
                          <button onClick={() => handleReport(otherParticipant(activeConversation))} className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="report-btn">
                            <Flag className="h-4 w-4" /> Signaler
                          </button>
                          <button onClick={() => handleBlock(otherParticipant(activeConversation))} className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-destructive flex items-center gap-2" data-testid="block-btn">
                            <ShieldOff className="h-4 w-4" /> Bloquer
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_email === user?.email ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.sender_email === user?.email ? 'bg-primary text-white' : 'bg-muted'}`}>
                        {m.type === 'voice' ? <VoiceMessageBubble url={m.media_url} /> : <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>}
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSend} className="p-3 border-t border-border flex items-center gap-2">
                  <VoiceRecorder onSend={handleSendVoice} />
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Écrire un message..."
                    className="flex-1 rounded-full border border-border px-4 py-2 text-sm bg-background"
                    data-testid="message-input"
                  />
                  <Button type="submit" size="icon" disabled={!input.trim()} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
